import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Typeahead, TypeaheadLabelKey } from 'react-bootstrap-typeahead';
import { Icon, Label, Typography } from '@packages/ui/shared';
import { getLogger } from '@packages/core/logging';
import { qmrsService } from '@web/services/singletons';
import colors from '@packages/core/styles/colors';
import { createUseStyles } from 'react-jss';
import './select-qmr-primary-objectives.css';

const logger = getLogger('web');

const useStyles = createUseStyles({
    dropDownContainer: {
        position: 'absolute',
        backgroundColor: colors.white,
        zIndex: 1,
        width: '100%',
        boxShadow: '0 7px 7px #ddd5d5',
        padding: '3px',
    },
    dropDownItem: {
        display: 'flex',
        alignItems: 'center',
        marginTop: '5px',
        cursor: 'pointer',
        padding: '10px',

        '&:hover': {
            backgroundColor: '#DEE6EF',
        },
    },
});

export const SelectQmrPrimaryObjectives = ({
    onSelect,
    defaultSelected = [],
    reportType,
    displayCategoryType = false,
}: any) => {
    const typeaheadRef = useRef<HTMLDivElement | null>(null);
    const classes = useStyles();

    const [qmrCategories, setQmrCategories] = useState([]);
    const [showDropDownMenu, setShowDropDownMenu] = useState(false);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            const target = event.target;
            if (target instanceof Node && typeaheadRef.current && !typeaheadRef.current.contains(target)) {
                setShowDropDownMenu(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleOnChange = useCallback(
        (selected: any) => {
            const currentQmrCategories: any = qmrCategories.map((category: any) => {
                if (selected.find((d: any) => d.qmrCategoryId === category.qmrCategoryId)?.qmrCategoryId) {
                    return { ...category, isSelected: true };
                }
                return { ...category, isSelected: false };
            });
            setQmrCategories(currentQmrCategories);
            setShowDropDownMenu(false);
        },
        [qmrCategories]
    );

    const fetchQmrCategories = async () => {
        try {
            const qmrCategories: any = await qmrsService.fetchQmrCategories(reportType);
            if (qmrCategories?.data?.QmrCategories?.length) {
                setQmrCategories(
                    qmrCategories.data.QmrCategories.map((category: any) => ({
                        ...category,
                        isSelected:
                            !!defaultSelected.find((c: any) => c.qmrCategoryId === category.qmrCategoryId)
                                ?.qmrCategoryId || qmrCategories.data.QmrCategories.length === 1,
                    }))
                );
            }
        } catch (err) {
            logger.error('Failed to fetch the QMR categories', { error: err });
        }
    };

    useEffect(() => {
        fetchQmrCategories();
    }, []);

    useEffect(() => {
        onSelect(qmrCategories.filter((d: { isSelected: any }) => d.isSelected));
    }, [qmrCategories]);

    const onOptionSelect = (option: any) => {
        const currentQmrCategories: any = qmrCategories.map((category: any) => {
            if (option.qmrCategoryId === category.qmrCategoryId) {
                return { ...category, isSelected: !category.isSelected };
            }
            return category;
        });
        setQmrCategories(currentQmrCategories);
        setShowDropDownMenu(false);
    };

    return (
        <>
            <Label required controlId="newQmr-retailer">
                What is the primary objective of submitting this report?
            </Label>
            <div ref={typeaheadRef}>
                <Typeahead
                    multiple
                    className=""
                    id="manage-multi-select-typeahead"
                    placeholder={'Select Objective'}
                    ref={null}
                    labelKey={'description' as TypeaheadLabelKey<string>}
                    options={qmrCategories}
                    onChange={handleOnChange}
                    selected={qmrCategories.filter((category: { isSelected: boolean }) => category.isSelected)}
                    open={showDropDownMenu}
                    onFocus={() => setShowDropDownMenu(true)}
                    renderMenu={() => (
                        <div className={classes.dropDownContainer}>
                            {qmrCategories.map((option: any, index) => {
                                return (
                                    <div
                                        data-testid="option"
                                        onClick={() => onOptionSelect(option)}
                                        className={classes.dropDownItem}
                                        key={option.qmrCategoryId}
                                    >
                                        <Icon
                                            color={'blueOne'}
                                            name={option.isSelected ? 'checkbox-marked' : 'checkbox-unmarked'}
                                        />
                                        <span className="ml-2">
                                            {option.description}
                                            {displayCategoryType && (
                                                <>
                                                    {' '}
                                                    <Typography variant="caption4">{`(${option.qmrCategoryType})`}</Typography>
                                                </>
                                            )}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                />
            </div>
        </>
    );
};
